import type {
  HotelResultsFilterValue,
  HotelSearchValue,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type {
  ChatUiHotelOffer,
  HotelPlannerBlock,
  HotelPlannerSelection,
  HotelRoomsRateFilters,
} from '@voll-intelligence/genui-contract';
import { drawerStay, initialPlace, staySelection } from './hotel_selection.js';
import { hotelSearchDateSummary } from './hotel_planner_view.js';
import { searchWaitProfile } from '../../../shared/ui/search_wait.js';
import type { HotelSearchOptionsPorts, useHotelSearchOptions } from './use_hotel_search_options.js';
import type { HotelSearchPreviewState } from './use_hotel_search_preview.js';
import { tapRefineContext } from './tap_refine.js';
import type { HotelPanelList } from './use_hotel_panel_list.js';
import type { PanelReport } from './use_panel_report.js';

/** Única fonte da entrada própria de ofertas do modo determinístico: quando
 *  `offers` está presente, devolve um bloco equivalente com `offers`
 *  substituído; senão devolve a MESMA referência de `ui` (zero diff no
 *  caminho conversacional). Chamada por todo consumidor que leria `ui.offers`
 *  cru — a view (lista/mapa), `sourceOffers`, e o `settled` da auto-busca
 *  (`selectionIsSettled`) — para o invariante "uma entrada, um só destino"
 *  ser estrutural, e não uma convenção espalhada por call site. */
export function withDeterministicOffers(
  ui: HotelPlannerBlock,
  offers: readonly ChatUiHotelOffer[] | undefined,
): HotelPlannerBlock {
  return offers ? { ...ui, offers: [...offers] } : ui;
}

/** O input de `useHotelSearchOptions`, fora do organismo (teto de linhas dele —
 *  ele já vive perto do limite do lint). */
export function hotelSearchOptionsInput(
  ui: HotelPlannerBlock,
  searchValue: HotelSearchValue,
  locale: string,
  ports: HotelSearchOptionsPorts | undefined,
): Parameters<typeof useHotelSearchOptions>[0] {
  return {
    ports: ports ?? {},
    initialTravelers: ui.travelers ?? [],
    initialPlace: initialPlace(ui),
    selectedPlaceId: searchValue.selectedPlaceId,
    selectedTravelerIds: searchValue.travelerIds,
    requesterId: ui.requesterId,
    locale,
  };
}

/** #5820 (CT-4): os chips de tarifa ligados na lista, no vocabulário do cartão
 *  de quartos — o recorte com que os quartos do hotel aberto dali nascem. */
export function listRateFilters(filters: HotelResultsFilterValue): HotelRoomsRateFilters {
  return {
    ...(filters.policyOnly ? { policy: true as const } : {}),
    ...(filters.breakfastOnly ? { breakfast: true as const } : {}),
    ...(filters.refundableOnly ? { refundable: true as const } : {}),
  };
}

/** A oferta PAGINADA vem primeiro: as linhas do painel e os pinos do mapa cheio
 *  são as da página, e cada uma tem handle próprio (a rota de página emite
 *  handles novos). Procurando só no bloco, tudo além das 6 dele era clique
 *  morto — a gaveta não abria e nada dizia por quê. O bloco segue como segunda
 *  fonte: é ele que está em cena antes de a primeira página chegar. O detalhe
 *  abre com o recorte de tarifa da lista (#5820). */
export function offerHandlers({
  list,
  sourceOffers,
  onOpenDetails,
}: {
  readonly list: Pick<HotelPanelList, 'sources' | 'query'>;
  readonly sourceOffers: readonly ChatUiHotelOffer[];
  readonly onOpenDetails: (offer: ChatUiHotelOffer, rateFilters: HotelRoomsRateFilters) => void;
}) {
  const openDetails = (hotelId: string) => {
    const offer =
      list.sources.find((candidate) => candidate.offerId === hotelId) ??
      sourceOffers.find((candidate) => candidate.offerId === hotelId);
    if (offer) onOpenDetails(offer, listRateFilters(list.query.filters));
  };
  return { openDetails };
}

/** Card superado: a busca não pode mais reescrever ESTE bloco (a conversa já
 *  andou por cima dele). Vira TURNO — mensagem do viajante + planner novo no
 *  rodapé —, e é o planner novo que congela este de vez (policy
 *  `freezeWhenSupersededBy`). O agents já sabe ler: o submit-guard do hotel
 *  parseia `selections` como `HotelPlannerSelection`. */
export function searchTurnDispatcher({
  ui,
  searchValue,
  locale,
  dispatch,
  t,
}: {
  readonly ui: HotelPlannerBlock;
  readonly searchValue: HotelSearchValue;
  readonly locale: string;
  /** Ausente na bancada determinística — lá não há conversa para virar turno, e
   *  quem gateia isso é o `superseded` que o organismo passa. */
  readonly dispatch: GenUiDispatch | undefined;
  readonly t: ReturnType<typeof useGenUiI18n>['t'];
}): (selection: HotelPlannerSelection, value?: HotelSearchValue) => void {
  return (selection, value = searchValue) => {
    const place =
      selection.place ?? selection.placeDetails?.name ?? selection.placeDetails?.city ?? '';
    const dates = hotelSearchDateSummary(value, locale);
    const profile = searchWaitProfile(selection.travelers, ui.requesterId);
    dispatch?.({
      type: 'interaction',
      interactionId: ui.challengeId,
      data: selection,
      text: t('hotelPlanner.searchText', { place, dates }),
      // The host's wait narrates the search with these (#5889).
      wait: { place: place || undefined, dates: dates || undefined, profile },
    });
  };
}

/* O card do dock CONTINUA A CONVERSA: escolher um hotel manda a seleção
   estruturada e o turno volta com o card do escolhido + os quartos, como no
   protótipo. O drawer não sai de cena — ele segue sendo o caminho do pino, da
   lista "Ver todos" e da tela cheia, que chamam `openDetails` direto. */
export function makeSelectHotel({
  sourceOffers,
  locked,
  dispatch,
  ui,
  previewState,
  screen,
  t,
}: {
  readonly sourceOffers: readonly ChatUiHotelOffer[];
  readonly locked: boolean;
  readonly dispatch: GenUiDispatch | undefined;
  readonly ui: HotelPlannerBlock;
  readonly previewState: HotelSearchPreviewState;
  /** M1 · 1.1b — o painel/total em cena, que o toque leva junto (ver `tapRefineContext`). */
  readonly screen: PanelReport;
  readonly t: ReturnType<typeof useGenUiI18n>['t'];
}) {
  return (hotelId: string) => {
    const offer = sourceOffers.find((candidate) => candidate.offerId === hotelId);
    if (!offer || locked || !dispatch) return;
    // As ofertas em cena saem do PREVIEW quando ele rodou, então a estadia que
    // as produziu tem que viajar junto: sem o eco o agente escolhe o hotel
    // certo com as datas anteriores ao refino.
    const stay = staySelection(drawerStay(ui, previewState.selection));
    // M1 · 1.1a: o toque leva a CENA junto (critérios + sessão + ofertas
    // enxutas) quando a busca rodou AQUI — a leitura client-side não vira
    // turno, então sem isto o agente decide sobre um FSM vazio. M1 · 1.1b:
    // o painel/total em cena viaja no mesmo `refine`.
    const refine = tapRefineContext(ui, previewState, screen);
    dispatch({
      type: 'hotel-select',
      offerId: offer.offerId,
      ...(offer.price ? { price: offer.price } : {}),
      ...(stay ? { stay } : {}),
      ...(refine ? { refine } : {}),
      text: t('hotel.selectText', { hotel: offer.title }),
    });
  };
}
